<script setup lang="ts">
import { getVersionId } from "@/shared/utils/misc/version";
import { ref } from "vue";
import { useBrowse } from "@/shared/mixins/useLink";
import { apiService } from "@/services/services";
import KlereoCaptcha from "@/shared/components/KlereoCaptcha.vue";
import config from "@/config";
import LoadingOverlay from "@/shared/components/LoadingOverlay.vue";
import LanguagePicker from "@/shared/components/LanguagePicker.vue";
import { variant } from "@/core/variant.ts";

const version = getVersionId();

const browse = useBrowse();

const loading = ref(false);
const error = ref<any>(null);
const success = ref(false);

const captchaSeed = ref(0);

const onSubmit = async ({
  login,
  pass,
  captcha,
}: {
  login: string;
  pass: string;
  captcha: string;
}) => {
  try {
    loading.value = true;
    error.value = null;
    const token = await apiService.login(login, pass);
    localStorage.setItem("token", token);
    await apiService.deleteUser({ captcha, pass });
    success.value = true;
  } catch (err: unknown) {
    console.error("oh no errror", err);
    error.value = err;
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div
    class="w-full m-5 md:w-[350px] md:m-auto self-center safe-area-inset-padding-top"
  >
    <loading-overlay :loading="loading" :error="error" />

    <div class="absolute text-xs bottom-0 right-0 text-gray-500 p-2">
      v{{ version }}
    </div>
    <div class="flex justify-center mb-5">
      <img
        src="@/assets/images/logo.svg"
        :alt="variant.appName"
        class="md:w-full"
      />
    </div>
    <div>
      <div class="card p-3 mb-5">
        <p class="text-center font-medium mb-5">
          {{ $t("DeleteAccountTitle") }}
        </p>

        <div v-if="success" class="text-sm text-gray-200">
          {{ $t("DeleteAccountSuccessText") }}
        </div>

        <FormKit
          v-else
          id="unregisterForm"
          type="form"
          @submit="onSubmit"
          :actions="false"
          validation-visibility="blur"
        >
          <div class="text-sm text-gray-200 mb-5">
            {{ $t("DeleteAccountConfirmationText") }}
          </div>

          <FormKit
            name="login"
            outer-class="mb-4"
            type="text"
            validation="required"
            :label="$t('Login')"
          />
          <div class="mb-4">
            <FormKit
              name="pass"
              type="password"
              validation="required"
              :label="$t('Pass')"
            />

            <div class="mt-3 text-right text-xs cursor-pointer">
              <a
                @click="
                  browse({
                    url: config.externalLinksRoot + '/v1/?page=forgot-password',
                  })
                "
              >
                {{ $t("Forget") }}
              </a>
            </div>
          </div>

          <klereo-captcha :refresh="captchaSeed" />
          <FormKit
            name="captcha"
            outer-class="mb-4"
            type="password"
            validation="required"
            :label="$t('EnterSecur')"
          />

          <div class="flex items-center justify-center">
            <FormKit
              type="submit"
              input-class="btn--primary bg-red-500"
              :label="$t('DeleteAccountButton')"
            />
          </div>
        </FormKit>
      </div>
    </div>

    <div class="mt-20 flex justify-center">
      <language-picker />
    </div>
  </div>
</template>
